// Page images shared by the browser (which prepares them, src/web/lib/prepare/) and the Worker (which checks and
// uses them, src/worker/import/). One uploaded page = the whole page image + up to PAGE_VIEWS zoomed crops for
// the independent price re-reading (DEC-064). The crop geometry is the same on both sides, so a page cut by the
// Worker (fallback for raw uploads) and a page cut by the browser give the model the same views.

/** 2 × 2 overlapping crops: about twice the detail per digit (first pilot, RAPORT_PILOT_AI_01 §8). */
export const PAGE_GRID = { cols: 2, rows: 2, overlap: 0.08 } as const;
export const PAGE_VIEWS = PAGE_GRID.cols * PAGE_GRID.rows;

/** Largest image sent to the model in one call (raw bytes; the data URL adds a third). */
export const MAX_PAGE_IMAGE_BYTES = 4 * 1_048_576;

export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Integer crop rectangles, row by row, clamped to the image (cells grow by `overlap` on every side). */
export function cropRects(
  width: number,
  height: number,
  { cols, rows, overlap }: { cols: number; rows: number; overlap: number } = PAGE_GRID,
): CropRect[] {
  const cw = width / cols;
  const ch = height / rows;
  const rects: CropRect[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = Math.max(0, Math.min(width - 1, Math.floor(c * cw - cw * overlap)));
      const y = Math.max(0, Math.min(height - 1, Math.floor(r * ch - ch * overlap)));
      rects.push({
        x,
        y,
        w: Math.max(1, Math.min(width - x, Math.floor(cw * (1 + 2 * overlap)))),
        h: Math.max(1, Math.min(height - y, Math.floor(ch * (1 + 2 * overlap)))),
      });
    }
  }
  return rects;
}

/**
 * Runs `fn` over `items` with at most `limit` calls in flight; results keep the order of `items`. After the
 * first failure no new call starts (calls already in flight finish) and the failure is thrown.
 */
export async function mapLimit<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  let failed = false;
  const worker = async () => {
    while (!failed && next < items.length) {
      const i = next++;
      try {
        results[i] = await fn(items[i] as T, i);
      } catch (e) {
        failed = true;
        throw e;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return results;
}
